/** Defaults and helpers for global chart display settings. */

export const CHART_DISPLAY_STORAGE_KEY = 'skyspeaks.chartDisplaySettings';

export const NAME_MODES = ['full', 'short', 'none'];
export const DEGREE_FORMATS = ['degree', 'dm', 'dms'];
export const SYMBOL_WEIGHTS = ['normal', 'bold'];
export const ANGLE_WEIGHTS = ['normal', 'bold'];
const SYMBOL_SCALE_MIN = 100;
const SYMBOL_SCALE_MAX = 200;

const LEGACY_LABEL_KEYS = {
  planetLabelMode: 'planetNameMode',
  signLabelMode: 'signNameMode',
  aspectLabelMode: 'aspectNameMode',
};

// Retired per-category weight fields, replaced by a single global
// symbolWeight. Kept only as legacy-normalization input keys, never part
// of the current defaults or normalized output.
const LEGACY_WEIGHT_KEYS = ['planetSymbolWeight', 'signSymbolWeight', 'aspectSymbolWeight'];
const LEGACY_WEIGHT_TO_SYMBOL_WEIGHT = { normal: 'normal', bold: 'bold', heavy: 'bold' };

export const DEFAULT_CHART_DISPLAY_SETTINGS = Object.freeze({
  planetNameMode: 'full',
  signNameMode: 'none',
  aspectNameMode: 'none',
  degreeFormat: 'dm',
  valueDecimalPlaces: 4,
  angleNameWeight: 'normal',
  symbolWeight: 'normal',
  planetSymbolScale: 115,
  sunSymbolScale: 115,
  moonSymbolScale: 115,
  signSymbolScale: 115,
  aspectSymbolScale: 120,
});

function normalizeNameMode(value) {
  if (value === 'symbol') {
    return 'none';
  }
  return value;
}

function normalizeDegreeFormat(value) {
  if (value === 'decimal') {
    return 'dm';
  }
  return value;
}

function coerceIncomingSettings(raw) {
  const coerced = {};
  for (const [key, value] of Object.entries(raw)) {
    if (key === 'decimalPlaces') {
      // Removed: zodiac decimal mode no longer exists.
      continue;
    }
    const mappedKey = LEGACY_LABEL_KEYS[key] || key;
    if (
      mappedKey === 'planetNameMode'
      || mappedKey === 'signNameMode'
      || mappedKey === 'aspectNameMode'
    ) {
      coerced[mappedKey] = normalizeNameMode(value);
    } else if (mappedKey === 'degreeFormat') {
      coerced[mappedKey] = normalizeDegreeFormat(value);
    } else {
      coerced[mappedKey] = value;
    }
  }
  return coerced;
}

/**
 * Migrate the retired per-category weight fields (planetSymbolWeight,
 * signSymbolWeight, aspectSymbolWeight) into the single global
 * symbolWeight field.
 *
 * Precedence: an explicit `symbolWeight` in the incoming payload wins as-is
 * (still subject to normal validation below); otherwise the legacy
 * `planetSymbolWeight` value migrates ("heavy" -> "bold", "normal"/"bold"
 * pass through); otherwise symbolWeight is left unset so the default
 * applies. Legacy signSymbolWeight/aspectSymbolWeight are intentionally
 * discarded without reconciliation -- one deterministic legacy source is
 * sufficient for a feature this recent.
 */
function migrateLegacySymbolWeight(coerced) {
  const result = { ...coerced };
  if (!('symbolWeight' in result)) {
    const legacy = result.planetSymbolWeight;
    if (Object.prototype.hasOwnProperty.call(LEGACY_WEIGHT_TO_SYMBOL_WEIGHT, legacy)) {
      result.symbolWeight = LEGACY_WEIGHT_TO_SYMBOL_WEIGHT[legacy];
    }
  }
  for (const key of LEGACY_WEIGHT_KEYS) {
    delete result[key];
  }
  return result;
}

function normalizePlaces(value, fallback) {
  const places = Number(value);
  if (!Number.isInteger(places) || places < 0 || places > 8) {
    return fallback;
  }
  return places;
}

function normalizeSymbolWeight(value, fallback) {
  return SYMBOL_WEIGHTS.includes(value) ? value : fallback;
}

function normalizeAngleWeight(value, fallback) {
  return ANGLE_WEIGHTS.includes(value) ? value : fallback;
}

function normalizeSymbolScale(value, fallback) {
  const scale = Number(value);
  if (!Number.isInteger(scale) || scale < SYMBOL_SCALE_MIN || scale > SYMBOL_SCALE_MAX) {
    return fallback;
  }
  return scale;
}

/**
 * @param {unknown} raw
 * @returns {{
 *   planetNameMode: string,
 *   signNameMode: string,
 *   aspectNameMode: string,
 *   degreeFormat: string,
 *   valueDecimalPlaces: number,
 *   angleNameWeight: string,
 *   symbolWeight: string,
 *   planetSymbolScale: number,
 *   sunSymbolScale: number,
 *   moonSymbolScale: number,
 *   signSymbolScale: number,
 *   aspectSymbolScale: number,
 * }}
 */
export function normalizeChartDisplaySettings(raw) {
  const base = { ...DEFAULT_CHART_DISPLAY_SETTINGS };
  if (!raw || typeof raw !== 'object') {
    return base;
  }

  const next = { ...base, ...migrateLegacySymbolWeight(coerceIncomingSettings(raw)) };

  if (!NAME_MODES.includes(next.planetNameMode)) {
    next.planetNameMode = base.planetNameMode;
  }
  if (!NAME_MODES.includes(next.signNameMode)) {
    next.signNameMode = base.signNameMode;
  }
  if (!NAME_MODES.includes(next.aspectNameMode)) {
    next.aspectNameMode = base.aspectNameMode;
  }
  if (!DEGREE_FORMATS.includes(next.degreeFormat)) {
    next.degreeFormat = base.degreeFormat;
  }

  next.valueDecimalPlaces = normalizePlaces(
    next.valueDecimalPlaces,
    base.valueDecimalPlaces,
  );

  next.angleNameWeight = normalizeAngleWeight(next.angleNameWeight, base.angleNameWeight);
  next.symbolWeight = normalizeSymbolWeight(next.symbolWeight, base.symbolWeight);
  next.planetSymbolScale = normalizeSymbolScale(next.planetSymbolScale, base.planetSymbolScale);
  next.sunSymbolScale = normalizeSymbolScale(next.sunSymbolScale, base.sunSymbolScale);
  next.moonSymbolScale = normalizeSymbolScale(next.moonSymbolScale, base.moonSymbolScale);
  next.signSymbolScale = normalizeSymbolScale(next.signSymbolScale, base.signSymbolScale);
  next.aspectSymbolScale = normalizeSymbolScale(next.aspectSymbolScale, base.aspectSymbolScale);

  return {
    planetNameMode: next.planetNameMode,
    signNameMode: next.signNameMode,
    aspectNameMode: next.aspectNameMode,
    degreeFormat: next.degreeFormat,
    valueDecimalPlaces: next.valueDecimalPlaces,
    angleNameWeight: next.angleNameWeight,
    symbolWeight: next.symbolWeight,
    planetSymbolScale: next.planetSymbolScale,
    sunSymbolScale: next.sunSymbolScale,
    moonSymbolScale: next.moonSymbolScale,
    signSymbolScale: next.signSymbolScale,
    aspectSymbolScale: next.aspectSymbolScale,
  };
}

export function readCachedChartDisplaySettings() {
  try {
    const raw = localStorage.getItem(CHART_DISPLAY_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return normalizeChartDisplaySettings(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function writeCachedChartDisplaySettings(settings) {
  try {
    localStorage.setItem(
      CHART_DISPLAY_STORAGE_KEY,
      JSON.stringify(normalizeChartDisplaySettings(settings)),
    );
  } catch {
    // Ignore quota / private-mode failures.
  }
}

export function clearCachedChartDisplaySettings() {
  try {
    localStorage.removeItem(CHART_DISPLAY_STORAGE_KEY);
  } catch {
    // Ignore.
  }
}
